import Head from "next/head";
import { BuilderShowCaseSection } from "components/CLM/BuilderShowCase";
import CLMHero2 from "components/CLM/CLMHero2";
import FAQ2 from "components/CLM/FAQ2";
import ProactiveCard from "components/CLM/ProactiveCard";
import { useEffect, useRef } from "react";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Secure, trackable clickwraps for high-scale workflows",
  description:
    "Let users to accept terms, policies, and agreements instantly, while you capture consent with legal proof and audit trails.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-hero.png`,
  btnText: "Request a Demo",
  btnUrl: "/clm/request-demo",
};

const showcaseData = [
  {
    heading: "Embed clickwrap to your site in 3 easy steps",
    subHeading:
      "Add clickwrap agreements to your site with a simple code snippet, no complex setup, dev hours, or custom tools required.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-embed-2.png`,
  },
  {
    heading: "Audit-ready records for every agreement",
    subHeading:
      "Capture every agreement with time, identity, and IP—fully auditable and legally enforceable.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-document-send-1.png`,
  },
  {
    heading: "Track consent across every version",
    subHeading:
      "Always show users the right contract, auto-save updates, and know exactly who accepted which version.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-terms-1.png`,
  },
  {
    heading: "Centralize your clickwrap lifecycle",
    subHeading:
      "One place to store, manage, and track every clickwrap agreement, with full visibility for legal teams.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-delta-1.png`,
  },
];

const clickThroughFaqData = [
  {
    question: "What is BoloSign Clickwrap and how does it work?",
    answer:
      "BoloSign Clickwrap lets users accept agreements (like Terms of Service or Privacy Policies) with a single click. You just embed a small code snippet on your site, and we handle consent tracking, versioning, and compliance for you.",
  },
  {
    question: "Is BoloSign Clickthrough legally enforceable?",
    answer:
      "Yes. Every click is backed by a verifiable audit trail and certificate of completion. That means you have solid proof in case of any dispute, enforceable under laws like the ESIGN Act and UETA.",
  },
  {
    question: "How do I implement clickwrap on my site?",
    answer:
      "It takes just one embed code. Copy, paste, and you’re live. No dev team dependency or any complex integrations.. And yes, it works with modern web frameworks like React, Webflow, and even no-code platforms.",
  },
  {
    question: "How does BoloSign handle updates to terms?",
    answer:
      "Every version of your clickwrap is tracked automatically. When you update the terms, users are shown the new version, and their consent is logged. You can easily see who accepted which version and when.",
  },
  {
    question: "Does BoloSign Clickthrough integrate with our existing systems?",
    answer:
      "Definitely. You can sync consent data with tools like HubSpot, Zapier, Segment, or use our API to push it to any system. BoloSign Clickwrap gives you full visibility into who accepted what, with filters by date, status, agreement type, and more.",
  },
  {
    question: "What kind of reporting or insights can I get?",
    answer:
      "You get real-time reports showing who agreed, when, from which device and location. Filter by date, domain, or even specific versions, all downloadable and audit-ready.",
  },
  {
    question: "Can I brand the clickwrap to match our company’s style?",
    answer:
      "Absolutely. You can host the clickwrap on your own domain, using your logo, fonts, and styling. It blends seamlessly with your site’s brand while maintaining legal integrity",
  },
];

const ClickThrough = ({ isDarkMode, setIsDarkMode }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const topTrigger = 120; // or some margin like 100
      const bottomTrigger = window.innerHeight;

      const isInView =
        rect.top <= topTrigger && rect.bottom >= bottomTrigger / 2;

      setIsDarkMode(isInView);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <>
      <Head>
        <title>
          Clickwrap Software for Legal Teams | BoloSign CLM Contract Management
        </title>
        <meta
          name="description"
          content="BoloSign Clickwrap lets legal teams create, manage, and track click-to-accept agreements easily. Capture user consent with audit trails and version control."
        />
        <meta
          name="keywords"
          content="Clickwrap software, click-to-accept agreements, user consent management, legal tech, BoloSign clickwrap, contract management, audit trails, version control"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/clickThrough/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto py-16 text-gray-900 text-center">
          <ProactiveCard
            // imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-centralized.gif")}
            videoSrc={`${BASE_ASSETS_URL}/v2/clm/videos/clickThrough-centralized.mp4`}
            title="Centralized terms management"
            desc="Quickly update, publish, and track your terms—ensuring users always access the latest version without IT delays."
            isImageRight={true}
            bgColor=""
          />
        </div>
      </section>

      <div
        className={`transition-colors duration-300  ${
          isDarkMode ? "bg-dark text-white" : "bg-white text-black"
        }`}
      >
        <section ref={sectionRef} className="pt-20">
          <BuilderShowCaseSection
            heading="Full control over every clickwrap"
            // Implementation, Audit Trial, Version Control, Legal
            categories={[
              "Implementation",
              "Audit Trial",
              "Version Control",
              "Legal",
            ]}
            showcaseData={showcaseData}
            isDarkMode={isDarkMode}
          />
        </section>
      </div>

      <div className="text-center bg-cream py-20">
        <h2 className="text-4xl md:text-6xl font-semibold mb-8 leading-relaxed">
          <span className="block">Why legal teams love</span>
          <span className="block md:mt-3">BoloSign clickwrap</span>
        </h2>
        <p className="text-xl lg:w-[70%] xl:w-[50%] mx-auto px-6 md:px-0">
          Built for legal teams to launch, track, and update clickwraps without
          relying on engineering.
        </p>
      </div>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto pt-16 md:pt-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-opensans.png`}
            title="Edit contracts like you write emails"
            desc="Make updates easily with a simple, no-training-needed editor, just like using Google Docs or Word."
            isImageRight={true}
            bgColor=""
          />
        </div>
      </section>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto pt-16 md:pt-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-disk.png`}
            title="Get contract insights instantly"
            desc="Auto-generate reports with key data like signers, versions, and timestamps, no manual tracking needed."
            isImageRight={false}
            bgColor=""
          />
        </div>
      </section>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto pt-16 md:pt-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-legal.png`}
            title="Host clickwraps on your own domain"
            desc="Reinforce brand trust and legal credibility by showing agreements directly from your website."
            isImageRight={true}
            bgColor=""
          />
        </div>
      </section>

      <section className="">
        <div className="max-w-screen-xl md:px-8 px-6 mx-auto py-16 md:py-32 text-gray-900 text-center">
          <ProactiveCard
            imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/clickThrough-logo.png`}
            title="Match every contract to your brand"
            desc="Add your logo, use custom fonts, and format contracts to reflect your company’s visual identity."
            isImageRight={false}
            bgColor=""
          />
        </div>
      </section>

      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={clickThroughFaqData} heading={"FAQs"} />
      </section>

      {/*  */}
    </>
  );
};

export default ClickThrough;
